import React, { useMemo, useState, useCallback } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Icon from '@amzn/meridian/icon';
import Tooltip from '@amzn/meridian/tooltip';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import bulbTokens from '@amzn/meridian-tokens/base/icon/bulb';
import { PlanDiffEntry, GetPlanDiffResponse, ItemType, PlanEntryInput } from '@graphql/generated/graphql';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import { timestampToDateStr, isIndefinite } from '@components/agile/utils/dateHelpers';
import useApplyItemsChange from '@store/hooks/useApplyItemsChange';
import useTableSort from 'src/hooks/useTableSort';
import usePagination from 'src/hooks/usePagination';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';
import HoverZoomImage from '../HoverZoomImage';
import TablePagination from '../TablePagination';
import TableSearchBar from '../TableSearchBar';
import CapacityValidationBanner, { CapacityValidationTarget } from '../CapacityValidationBanner';
import '../ScrollableTable.css';

export interface ItemBulkUploadDiffTableProps {
  diffResult: GetPlanDiffResponse;
  subcategories: storeSubcategory[];
  storeId: string;
  macroplanogramId: string;
  macroplanogramEndTime: number;
  swapTime: number;
  onSaveSuccess?: () => void;
  onBack?: () => void;
}

type DiffStatus = 'New' | 'Ended' | 'Extended' | 'Deleted';

interface FlatDiffEntry {
  status: DiffStatus;
  entry: PlanDiffEntry;
}

import { toTitleCase, replaceUuidsWithNames } from '@components/agile/utils/stringUtils';

const formatTimestamp = (ts: number | string): string => {
  const num = typeof ts === 'string' ? Number(ts) : ts;
  if (Number.isNaN(num) || isIndefinite(num)) {
    return 'Indefinite';
  }
  return timestampToDateStr(num);
};

const flattenEntries = (diffResult: GetPlanDiffResponse): FlatDiffEntry[] => {
  const entries: FlatDiffEntry[] = [];
  diffResult.newEntries?.forEach((e) => entries.push({ status: 'New', entry: e }));
  diffResult.endedEntries?.forEach((e) => entries.push({ status: 'Ended', entry: e }));
  diffResult.extendedEntries?.forEach((e) => entries.push({ status: 'Extended', entry: e }));
  diffResult.deletedEntries?.forEach((e) => entries.push({ status: 'Deleted', entry: e }));
  return entries;
};

const toPlanEntry = (entry: PlanDiffEntry): PlanEntryInput => ({
  itemId: entry.itemId,
  itemType: entry.itemType,
  subcategoryId: entry.subcategoryId ?? undefined,
  categoryId: entry.categoryId ?? undefined,
});

/**
 * Maps a plan diff into capacity-validation deltas:
 *  - itemsToAdd    = items being added (New)
 *  - itemsToDelete = items leaving the post-swap snapshot (Ended + Deleted)
 * Extended entries already occupy space, so they are a no-op. This mapping is
 * bulk-upload-specific; the banner itself is flow-agnostic.
 */
const buildCapacityTarget = (
  storeId: string,
  macroplanogramId: string,
  startTime: number,
  diff: GetPlanDiffResponse,
): CapacityValidationTarget => ({
  storeId,
  macroplanogramId,
  startTime,
  itemsToAdd: (diff.newEntries ?? []).map(toPlanEntry),
  itemsToDelete: [...(diff.endedEntries ?? []), ...(diff.deletedEntries ?? [])].map(toPlanEntry),
});

interface SortableRow {
  key: string;
  status: string;
  itemId: string;
  title: string;
  subcategory: string;
  startTime: string;
  endTime: string;
  image: string | null;
  originalEntry: PlanDiffEntry;
}

const ItemBulkUploadDiffTable = ({
  diffResult,
  subcategories,
  storeId,
  macroplanogramId,
  macroplanogramEndTime,
  swapTime,
  onSaveSuccess,
  onBack,
}: ItemBulkUploadDiffTableProps) => {
  const config = useProgramConfig();
  const itemIdentifierLabel = config.labels.itemIdentifierType;
  const subcategoryLabel = config.labels.subcategory;
  const categoryLabel = config.labels.category;
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const scopeColumnLabel = scope === 'subcategory' ? subcategoryLabel : categoryLabel;
  const isCapacityValidationEnabled = !!config.features.capacityValidation;
  const subcategoryMap = useMemo(
    () => new Map(subcategories.map((sc) => [sc.subcategoryId, sc.subcategoryName])),
    [subcategories],
  );

  // Local mutable copy of diff lists so entries can be removed
  const [localNew, setLocalNew] = useState<PlanDiffEntry[]>(() => [...(diffResult.newEntries || [])]);
  const [localEnded, setLocalEnded] = useState<PlanDiffEntry[]>(() => [...(diffResult.endedEntries || [])]);
  const [localExtended, setLocalExtended] = useState<PlanDiffEntry[]>(() => [...(diffResult.extendedEntries || [])]);
  const [localDeleted, setLocalDeleted] = useState<PlanDiffEntry[]>(() => [...(diffResult.deletedEntries || [])]);

  const localDiff: GetPlanDiffResponse = useMemo(
    () => ({
      newEntries: localNew,
      endedEntries: localEnded,
      extendedEntries: localExtended,
      deletedEntries: localDeleted,
    }),
    [localNew, localEnded, localExtended, localDeleted],
  );

  const allEntries = useMemo(() => flattenEntries(localDiff), [localDiff]);

  // Bulk-upload deltas for capacity validation, derived from the current
  // (edited) diff. Only consumed when the capacity feature is enabled.
  const capacityTarget = useMemo(
    () => buildCapacityTarget(storeId, macroplanogramId, swapTime, localDiff),
    [storeId, macroplanogramId, swapTime, localDiff],
  );

  const handleRemoveEntry = useCallback((status: DiffStatus, entry: PlanDiffEntry) => {
    const filterOut = (list: PlanDiffEntry[]) => list.filter((e) => e !== entry);
    switch (status) {
      case 'New':
        setLocalNew(filterOut);
        break;
      case 'Ended':
        setLocalEnded(filterOut);
        break;
      case 'Extended':
        setLocalExtended(filterOut);
        break;
      case 'Deleted':
        setLocalDeleted(filterOut);
        break;
    }
  }, []);

  // Build sortable rows
  const rows: SortableRow[] = useMemo(
    () =>
      allEntries.map(({ status, entry }, idx) => ({
        key: entry.entryId || `${entry.itemId}-${idx}`,
        status,
        itemId: entry.itemId,
        title: entry.itemMetaDataEntry?.title || 'Unknown',
        subcategory:
          scope === 'subcategory'
            ? toTitleCase(subcategoryMap.get(entry.subcategoryId ?? '') || entry.subcategoryId || '')
            : (entry.categoryId ?? ''),
        startTime: formatTimestamp(entry.startTime),
        endTime: formatTimestamp(entry.endTime),
        image: entry.itemMetaDataEntry?.image || null,
        originalEntry: entry,
      })),
    [allEntries, subcategoryMap, scope],
  );

  const { sortColumn, sortDirection, onSort, sortData } = useTableSort<SortableRow>('status');
  const sortedRows = useMemo(() => sortData(rows), [sortData, rows]);

  const [searchValue, setSearchValue] = useState('');
  const filteredRows = useMemo(() => {
    if (!searchValue) return sortedRows;
    const query = searchValue.toLowerCase();
    return sortedRows.filter(
      (row) => row.itemId.toLowerCase().includes(query) || row.title.toLowerCase().includes(query),
    );
  }, [sortedRows, searchValue]);

  const pagination = usePagination({ totalItems: filteredRows.length });
  const paginatedRows = useMemo(
    () => filteredRows.slice(pagination.visibleIndices[0], pagination.visibleIndices[1]),
    [filteredRows, pagination.visibleIndices],
  );

  const getEndDateTooltip = useCallback(
    (status: string, endTime: number): string | null => {
      const numEndTime = typeof endTime === 'string' ? Number(endTime) : endTime;
      if (isIndefinite(numEndTime)) {
        return 'This zone map has no end date. The assortment end date is set to the maximum allowed value.';
      }
      if (status === 'New' || status === 'Extended') {
        return `This end date matches the zone map end date (${timestampToDateStr(macroplanogramEndTime)}), which is the latest allowed date for this assortment.`;
      }
      if (status === 'Ended') {
        return `This end date matches the selected start date for the new plan (${timestampToDateStr(swapTime)}).`;
      }
      if (status === 'Deleted') {
        return `This item is being deleted before its live date. This date (${formatTimestamp(endTime)}) was the original end date for the item.`;
      }
      return null;
    },
    [macroplanogramEndTime, swapTime],
  );

  const { mutateAsync, isLoading: isSaving } = useApplyItemsChange();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [partialErrors, setPartialErrors] = useState<Array<{ key: string; message: string }>>([]);
  const [showPartialErrorModal, setShowPartialErrorModal] = useState(false);

  const downloadErrorsCsv = useCallback(() => {
    const header = 'Scancode,Error Message';
    const csvRows = partialErrors.map((e) => `${e.key},"${e.message.replace(/"/g, '""')}"`);
    const csv = [header, ...csvRows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'failed_items.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [partialErrors]);

  const handlePartialErrorOk = useCallback(() => {
    setShowPartialErrorModal(false);
    setSaveSuccess(true);
    onSaveSuccess?.();
  }, [onSaveSuccess]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setPartialErrors([]);

    const newEntries = localNew.map((e) => ({
      itemId: e.itemId,
      itemType: e.itemType as ItemType,
      subcategoryId: e.subcategoryId,
      categoryId: e.categoryId,
      startTime: e.startTime,
      endTime: e.endTime,
    }));

    const updateEntries = [...localEnded, ...localExtended].map((e) => ({
      entryId: e.entryId!,
      optimisticLockId: e.optimisticLockId ?? undefined,
      itemId: e.itemId,
      itemType: e.itemType as ItemType,
      subcategoryId: e.subcategoryId,
      categoryId: e.categoryId,
      startTime: e.startTime,
      endTime: e.endTime,
    }));

    const deleteEntryIds = localDeleted.filter((e) => e.entryId).map((e) => e.entryId!);

    try {
      const result = await mutateAsync({
        input: {
          storeId,
          macroplanogramId,
          newEntries: newEntries.length > 0 ? newEntries : undefined,
          updateEntries: updateEntries.length > 0 ? updateEntries : undefined,
          deleteEntryIds: deleteEntryIds.length > 0 ? deleteEntryIds : undefined,
        },
      });
      const errors = result?.applyItemsChange?.errors ?? [];
      if (errors.length > 0) {
        // Resolver-level errors have underscore-prefixed keys (e.g. _crossList, _write).
        // These mean the entire request was rejected before any writes happened.
        const resolverErrors = errors.filter((e) => e.key.startsWith('_'));
        // AMS write errors use itemId as key — these are partial failures where
        // some items were written successfully but others failed.
        const amsErrors = errors.filter((e) => !e.key.startsWith('_'));

        if (resolverErrors.length > 0) {
          setSaveError(
            resolverErrors
              .map((e) => `${e.key}: ${replaceUuidsWithNames(e.error.errorMessage, subcategoryMap)}`)
              .join('; '),
          );
        } else if (amsErrors.length > 0) {
          setPartialErrors(
            amsErrors.map((e) => ({
              key: e.key,
              message: replaceUuidsWithNames(e.error.errorMessage, subcategoryMap),
            })),
          );
          setShowPartialErrorModal(true);
        }
      } else {
        setSaveSuccess(true);
        onSaveSuccess?.();
      }
    } catch (err) {
      setSaveError((err as Error)?.message ?? 'An unexpected error occurred while saving.');
    }
  }, [
    localNew,
    localEnded,
    localExtended,
    localDeleted,
    storeId,
    macroplanogramId,
    mutateAsync,
    onSaveSuccess,
    subcategoryMap,
  ]);

  if (allEntries.length === 0) {
    return (
      <Column spacingInset="400">
        <Text type="h400">No differences found. The plan matches the current assortment.</Text>
      </Column>
    );
  }

  return (
    <Column spacing="400" spacingInset="300">
      <Text type="h400">Plan Diff Summary</Text>

      {/* Capacity Validation Alert (GFC-gated). Rendered only when the feature
          is enabled, so the validation query fires exactly when this UI shows. */}
      {isCapacityValidationEnabled && <CapacityValidationBanner target={capacityTarget} />}

      <TableSearchBar value={searchValue} onChange={setSearchValue} />
      <div className="scrollable-table-container">
        <Table
          headerRows={1}
          showDividers
          spacing="small"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableRow backgroundColor="secondary">
            <TableCell sortColumn="status">Status</TableCell>
            <TableCell sortColumn="itemId">{itemIdentifierLabel}</TableCell>
            <TableCell>Image</TableCell>
            <TableCell sortColumn="title">Title</TableCell>
            {scope === 'subcategory' && <TableCell sortColumn="subcategory">{scopeColumnLabel}</TableCell>}
            <TableCell sortColumn="startTime">Start</TableCell>
            <TableCell sortColumn="endTime">End</TableCell>
            <TableCell>Remove</TableCell>
          </TableRow>
          {paginatedRows.map((row) => (
            <TableRow key={row.key} highlightOnHover>
              <TableCell>{row.status}</TableCell>
              <TableCell>{row.itemId}</TableCell>
              <TableCell>
                {row.image ? <HoverZoomImage src={row.image} alt={row.title} width={32} height={32} /> : '—'}
              </TableCell>
              <TableCell>{row.title}</TableCell>
              {scope === 'subcategory' && <TableCell>{row.subcategory}</TableCell>}
              <TableCell>{row.startTime}</TableCell>
              <TableCell>
                <Row spacing="none" alignmentVertical="center">
                  {row.endTime}
                  {getEndDateTooltip(row.status, row.originalEntry.endTime) && (
                    <Tooltip position="top" title={getEndDateTooltip(row.status, row.originalEntry.endTime)!}>
                      <Icon tokens={bulbTokens} />
                    </Tooltip>
                  )}
                </Row>
              </TableCell>
              <TableCell>
                <Button
                  type="icon"
                  size="small"
                  onClick={() => handleRemoveEntry(row.status as DiffStatus, row.originalEntry)}
                  aria-label={`Remove ${row.itemId}`}
                >
                  <Icon tokens={closeKnockoutTokens} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </div>

      <TablePagination pagination={pagination} totalItems={filteredRows.length} />

      {saveError && (
        <Alert type="error" size="small">
          {saveError}
        </Alert>
      )}

      <Row alignmentHorizontal="end" spacingInset="300">
        {onBack && (
          <Button type="secondary" onClick={onBack} disabled={isSaving || saveSuccess}>
            Back
          </Button>
        )}
        <Button type="primary" disabled={isSaving || saveSuccess} onClick={handleSave}>
          {isSaving ? <Loader type="circular" size="small" /> : 'Save Changes'}
        </Button>
      </Row>

      <Modal
        open={showPartialErrorModal}
        scrollContainer="viewport"
        onClose={handlePartialErrorOk}
        aria-describedby="partial-error-description"
      >
        <Text type="h400">Partial Save Completed</Text>
        <Column spacingInset="300 none none none">
          <Text type="b300" id="partial-error-description">
            {partialErrors.length} {partialErrors.length === 1 ? 'item' : 'items'} failed to save. The remaining items
            were applied successfully. Download the error report for details.
          </Text>
        </Column>
        <ModalFooter>
          <Row spacing="400" alignmentHorizontal="end" width="100%">
            <Button type="secondary" onClick={downloadErrorsCsv}>
              Download Errors
            </Button>
            <Button type="primary" onClick={handlePartialErrorOk}>
              OK
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </Column>
  );
};

export default ItemBulkUploadDiffTable;
